// Самостоятельная смена пароля пользователем. Бэкенд: POST /auth/change-password
// {current_password, new_password} — проверяет текущий, отзывает другие сессии,
// текущую оставляет. Доступно любому залогиненному (в т.ч. заказчику).
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { Modal } from '@/components/Modal';

export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);

  const localErr =
    next && next.length < 8 ? 'Новый пароль — минимум 8 символов.'
    : confirm && confirm !== next ? 'Новый пароль и подтверждение не совпадают.'
    : null;

  const submit = useMutation({
    mutationFn: () => api.post('/auth/change-password', { current_password: cur, new_password: next }),
    onSuccess: () => setDone(true),
  });

  const errText = submit.isError
    ? ((submit.error as ApiError | undefined)?.status === 401 ? 'Текущий пароль неверный.'
       : (submit.error as ApiError | undefined)?.status === 422 ? 'Новый пароль слишком короткий (минимум 8 символов).'
       : 'Не удалось сменить пароль. Попробуйте ещё раз.')
    : null;

  const canSubmit = cur.length > 0 && next.length >= 8 && confirm === next && !submit.isPending;

  if (done) {
    return (
      <Modal title="Пароль изменён" onClose={onClose} maxWidth={440}
        footer={<button type="button" onClick={onClose} className="rounded-pill" style={{ padding: '9px 20px', fontSize: 14, fontWeight: 600, background: '#0A1628', color: '#fff', border: 'none', cursor: 'pointer' }}>Готово</button>}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <CheckCircle2 size={22} style={{ color: '#059669', flexShrink: 0, marginTop: 2 }} />
          <div className="text-ink" style={{ fontSize: 14, lineHeight: 1.55 }}>
            Пароль обновлён. Остальные сеансы (на других устройствах) завершены — текущий вход сохранён.
          </div>
        </div>
      </Modal>
    );
  }

  const field = (label: string, value: string, set: (v: string) => void, ph: string, autoComplete: string) => (
    <div style={{ marginBottom: 14 }}>
      <div className="text-muted" style={{ fontSize: 12, fontWeight: 600, marginBottom: 5 }}>{label}</div>
      <input
        type="password"
        value={value}
        onChange={(e) => set(e.target.value)}
        placeholder={ph}
        autoComplete={autoComplete}
        style={{ width: '100%', fontSize: 14, padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: 8, outline: 'none', boxSizing: 'border-box' }}
      />
    </div>
  );

  return (
    <Modal
      title="Сменить пароль"
      onClose={onClose}
      maxWidth={440}
      footer={
        <div className="flex items-center" style={{ gap: 8 }}>
          <button type="button" onClick={onClose} className="rounded-pill text-muted" style={{ padding: '9px 18px', fontSize: 14, fontWeight: 600, background: '#fff', border: '1px solid #E5E7EB', cursor: 'pointer' }}>Отмена</button>
          <button type="button" onClick={() => canSubmit && submit.mutate()} disabled={!canSubmit} className="rounded-pill"
            style={{ padding: '9px 18px', fontSize: 14, fontWeight: 600, border: 'none',
                     background: canSubmit ? '#0A1628' : '#F3F4F6', color: canSubmit ? '#fff' : '#9CA3AF',
                     cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
            {submit.isPending ? 'Сохраняю…' : 'Сменить пароль'}
          </button>
        </div>
      }
    >
      <p className="text-muted" style={{ fontSize: 12.5, lineHeight: 1.5, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <KeyRound size={14} style={{ flexShrink: 0 }} /> После смены другие сеансы (на других устройствах) будут завершены.
      </p>
      {field('Текущий пароль', cur, setCur, 'введите текущий пароль', 'current-password')}
      {field('Новый пароль', next, setNext, 'минимум 8 символов', 'new-password')}
      {field('Повторите новый пароль', confirm, setConfirm, 'ещё раз новый пароль', 'new-password')}
      {(localErr || errText) && (
        <p style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 2 }}>{errText || localErr}</p>
      )}
    </Modal>
  );
}
